import React, { Fragment } from "react";
import { styled } from '@mui/material/styles';
import MapIcon from "@mui/icons-material/Map";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "@mui/material/Button";
import Box from '@mui/material/Box';
import {
  faExternalLinkAlt,
  faDatabase,
  faGlobeAmericas,
  faFileContract,
  faFileExcel,
  faChartBar,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import hazusIcon from "../../../../assets/images/hazus.png";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";

import handleGA, { gaEvents } from "../../../googleAnalytics";

const PREFIX = 'Download';

const classes = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  download: `${PREFIX}-download`,
  icon: `${PREFIX}-icon`,
  link: `${PREFIX}-link`,
  popup: `${PREFIX}-popup`,
  popupHeader: `${PREFIX}-popupHeader`,
  popupContent: `${PREFIX}-popupContent`,
  popupDescription: `${PREFIX}-popupDescription`,
  downloadButton: `${PREFIX}-downloadButton`,
  meta: `${PREFIX}-meta`,
  metaName: `${PREFIX}-metaName`,
  close: `${PREFIX}-close`
};

const Root = styled('div')((
  {
    theme
  }
) => ({

  [`& .${classes.header}`]: {
    textTransform: "capitalize",
    color: "#999999",
    margin: "0.5em 0",
    display: "block",
    fontSize: "0.8rem",
  },

  [`& .${classes.download}`]: {
    display: "flex",
    alignItems: "center",
    margin: "0.5em 0",
    color: "#005287",
    border: "1px solid #e1e1e1 !important",
    borderRadius: "50px",
    padding: "2px 8px",
    textAlign: "left",
    textTransform: "none",
	whiteSpace: "nowrap",
    "&:hover": {
      boxShadow: "1px 1px 4px #a1a1a1",
      background: "white",
    },
  },

  [`& .${classes.icon}`]: {
    height: "1.5em",
    marginRight: "0.5em",
  },

  [`& .${classes.link}`]: {
    textDecoration: "none",
    fontSize: "0.9rem",
    wordWrap: "normal",
  },

  [`& .${classes.popup}`]: {
    maxWidth: "20em",
    fontFamily: '"Roboto", sans-serif',
  },

  [`& .${classes.popupHeader}`]: {
    margin: 0,
    padding: "0.2em 2em 0.2em 1em",
    background: "#0078a9",
    color: "#fff",
    display: "flex",
    fontSize: "1em",
  },

  [`& .${classes.popupContent}`]: {
    padding: "1em",
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.popupDescription}`]: {},

  [`& .${classes.downloadButton}`]: {
    alignSelf: "flex-end",
    background: "#0078a9",
    color: "#fff",
    marginTop: "2em",
    textTransform: "capitalize",
    "&:hover": {
      background: "#00709e",
      color: "#fff",
      boxShadow: "1px 1px 4px #a1a1a1",
    },
  },

  [`& .${classes.meta}`]: {
    display: "block",
    fontSize: "0.8rem",
    margin: "0.3em 0",
  },

  [`& .${classes.metaName}`]: {
    fontWeight: "bold",
    marginRight: "0.3em",
  },

  [`& .${classes.close}`]: {
    "&:hover": {
      cursor: "pointer",
      color: "#f9f9f9",
      boxShadow: "1px 1px 4px #505050",
    },
  }

}));

const api = process.env.REACT_APP_API;

const Download = ({ download }) => {
  const { name, icon, link, file, meta } = download;

  // filters file names to remove extra file extensions 
  function getFileDownloadName(file) {
    // removes everything before the last backslash
    var filename = file.split("/").pop();
    // creates array based on .
    var nameArray = filename.split(".");
    // case where there is an extra file extension
    if (nameArray.length == 3){
      return nameArray.slice(0,2).join(".");
    }
    // case where there is only the file name and extension
    if (nameArray.length == 2){
      return filename;
    }
  }

  function normalizeLink(link) {
    // if the link button will be rendered over the download button
    if (link.length > 1) {
      // remove white space
      const trimmedLink = link.trim();

      // if the link already contains https or http then do nothing
      if (/^https?:\/\//i.test(trimmedLink)) {
        return trimmedLink;
      }

      // return the link with added https protocol if not present
      return `https://${trimmedLink}`;
    }
  }

  const normalizedLink = normalizeLink(link);

  const styleCloseIcon = {
	color: "#fff",
    position: "absolute",
    right: "0.5em",
    top: "0.5em",
    borderRadius: "50%",
  };

  const icons = {
    hazus: <img className={classes.icon} src={hazusIcon} alt="icon" />,
    data: <FontAwesomeIcon className={classes.icon} icon={faDatabase} />,
    report: <FontAwesomeIcon className={classes.icon} icon={faFileContract} />,
    dashboard: <FontAwesomeIcon className={classes.icon} icon={faChartBar} />,
    map: <MapIcon className={classes.icon} />,
    spreadsheet: (
      <FontAwesomeIcon className={classes.icon} icon={faFileExcel} />
    ),
    spatial: (
      <FontAwesomeIcon className={classes.icon} icon={faGlobeAmericas} />
    ),
    link: <FontAwesomeIcon className={classes.icon} icon={faExternalLinkAlt} />,
  };

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadFile = (file) => {
    // Google Analytics
    handleGA(gaEvents.DOWNLOAD_FILE, `${file}`);
  };

  const handleDownloadLink = (file) => {
    // Google Analytics
    handleGA(gaEvents.DOWNLOAD_LINK, `${file}`);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <Root className={classes.root}>
      <Button
        className={classes.download}
        onClick={handleOpen}
        aria-label={name}
      >
        {icons[icon]}
        <span>{name}</span>
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}>
        <Typography className={classes.popup}
		sx={{
			maxWidth: "20em",
		    fontFamily: '"Roboto", sans-serif',
		}}>
		<Box 
		className={classes.close}
		sx={{
			"&:hover": {
				cursor: "pointer",
				color: "#f9f9f9",
				boxShadow: "1px 1px 4px #505050",
    		},
		}}>
	          <FontAwesomeIcon
				style={styleCloseIcon}
	            icon={faTimesCircle}
	            onClick={handleClose}
	            alt="close"
	            aria-label="close download/view popup"
	          />
		</Box>
		  <Box component="h3" className={classes.popupHeader}
		  	sx={{
				margin: 0,
			    padding: "0.2em 2em 0.2em 1em",
			    background: "#0078a9",
			    color: "#fff",
			    display: "flex",
			    fontSize: "1em",
			}}>
	            <Box sx={{height: "1.5em", marginRight: "0.5em",}}> {icons[icon]}</Box> {name}
		  </Box>
          <Box className={classes.popupContent}>
            <Box className={classes.popupDescription}>
              {Object.keys(meta).map((key) => {
                return (
                  <Box className={classes.meta}>
                    <Box component="span" className={classes.metaName}>{key}:</Box>
                    <Box component="span" className={classes.metaInfo}>
                      {meta[key].toString()}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {link.length > 1 ? (
			  <Button className={classes.downloadButton}
	                onClick={() => handleDownloadLink(file)}
	                href={`${normalizedLink}`}
	                target="_blank"
	                aria-label={`go to ${normalizedLink}`}
					sx={{
						letterSpacing: "0.02857em",
						userSelect: "none",
						minWidth: "64px",
						whiteSpace: "nowrap",
						margin: "10%",
						cursor: "pointer",
						fontSize: "0.875rem",
						borderRadius: "4px",
						padding: "6px 8px",
						lineHeight: "1.75",
						alignSelf: "flex-end",
					    background: "#0078a9",
					    color: "#fff",
					    marginTop: "2em",
					    textTransform: "capitalize",
					    "&:hover": {
					      background: "#00709e",
					      color: "#fff",
					      boxShadow: "1px 1px 4px #a1a1a1",
   						 },
					}}>
	                download / view
			  </Button>
            ) : (
			  <Button className={classes.downloadButton}
	                onClick={() => handleDownloadFile(file.replace(/[^a-zA-Z0-9 _./]/g, ''))}
	                href={`${api}${file.replace(/[^a-zA-Z0-9 _./]/g, '')}`}
                  download={getFileDownloadName(file).replace(/[^a-zA-Z0-9 _./]/g, '')}
	                target="_blank"
	                aria-label={`download ${file.replace(/[^a-zA-Z0-9 _./]/g, '')}`}
					sx={{
						letterSpacing: "0.02857em",
						userSelect: "none",
						minWidth: "64px",
						whiteSpace: "nowrap",
						margin: "10%",
						cursor: "pointer",
						fontSize: "0.875rem",
						borderRadius: "4px",
						padding: "6px 8px",
						lineHeight: "1.75",
						alignSelf: "flex-end",
					    background: "#0078a9",
					    color: "#fff",
					    marginTop: "2em",
					    textTransform: "capitalize",
					    "&:hover": {
					      background: "#00709e",
					      color: "#fff",
					      boxShadow: "1px 1px 4px #a1a1a1",
   						 },
					}}>
	                download / view
			  </Button>
            )}
          </Box>
        </Typography>
      </Popover>
    </Root>
  );
};

export default Download;
